import React, { useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./style";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { User } from "model/user";
import { FeatherIcon } from "component/shared/feather-icon";
import { MenuItem } from "../menu-item";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { getActive, getOpen } from "redux/selector";
import { openChange } from "redux/slice/sidebar-menu";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";

interface Props extends
	Router.RouteComponentProps,
	WithStyles<typeof styles>,
	WithMediaQuery
{
	item: MenuItem;
	user: User;
}

const Component: React.FC<Props> = ({ user, item, classes = {}, match, mediaQuery }) => {
	const activeSelector = useSelector(getActive);
	const openSelector = useSelector(getOpen);
	const dispatch = useAppThunkDispatch();

	const id = item.id || "";

	const parentId = useMemo(() => {
		const parts = id.split(".");
		parts.pop();
		return parts.join(".");
	}, [id]);

	const level = useMemo(() => id.split(".").length - 1, [id]);

	const active = useMemo(() => activeSelector === id, [activeSelector, id]);

	const childIsActive = useMemo(() =>
		!!activeSelector && activeSelector !== id && activeSelector.startsWith(id + "."),
	[activeSelector, id]);

	const open = useMemo(() => openSelector ? openSelector.startsWith(id) : false, [openSelector, id]);

	const sidebarCollapsed = mediaQuery;

	const toggle = useCallback(() => {
		if (open) {
			dispatch(openChange(parentId));
		} else {
			dispatch(openChange(id));
		}
	}, [open, parentId, id, dispatch]);

	const firstChild = useMemo(() => {
		if (item.children) {
			return item.children.find((item) => item.path && item.label);
		}
		return undefined;
	}, [item.children]);

	const backgroundOpacity = useMemo(() => {
		if (!sidebarCollapsed && level === 1 && (!open || active)) {
			return 0;
		}
		if (!active && open) {
			return (level + 1) * .05;
		} else {
			return level * .05;
		}
	}, [active, open, level, sidebarCollapsed]);

	const paddingLeft = useMemo(() => level * 16, [level]);

	const renderLabel = useMemo(() => (
		<>
			{item.icon && (
				<FeatherIcon className={classes.icon}>
					<item.icon />
				</FeatherIcon>
			)}
			{item.label}
		</>
	), [item.icon, item.label, classes.icon]);

	const renderChevron = useMemo(() => (
		<FeatherIcon
			className={classes.chevron}
			onClick={(e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); toggle(); }}
		>
			{open ? (
				<Icons.ChevronUp size={16} />
			) : (
				<Icons.ChevronDown size={16} />
			)}
		</FeatherIcon>
	), [open, classes.chevron, toggle]);

	const renderInternalLink = useMemo(() => (
		<Mui.Button
			className={classNames(
				classes.item,
				active && classes.active,
				!active && classes.inactive,
			)}
			style={{
				...(level > 0 && { paddingLeft }),
			}}
			variant={active ? "contained" : undefined}
			color={active ? "secondary" : undefined}
			component={RouterLinkWrapper}
			to={item.path}
		>
			{renderLabel}
		</Mui.Button>
	), [classes.item, classes.active, classes.inactive, active, paddingLeft, item.path, renderLabel]);

	const renderExternalLink = useMemo(() => (
		<Mui.Button
			className={classes.item}
			style={{
				paddingLeft,
			}}
			href={item.path || ""}
			target={item.target}
		>
			{renderLabel}
		</Mui.Button>
	), [classes.item, paddingLeft, item.path, item.target, renderLabel]);

	const renderGroup = useMemo(() => {
		const groupClassName = classNames(classes.item, childIsActive ? classes.groupItemChildActive : classes.inactive);
		if (!open && firstChild && !sidebarCollapsed) {
			return (
				<Mui.Button
					className={groupClassName}
					style={{ paddingLeft }}
					component={RouterLinkWrapper}
					to={firstChild.path}
				>
					{renderLabel}
					{item.children && renderChevron}
				</Mui.Button>
			);
		} else {
			return (
				<Mui.Button
					className={groupClassName}
					style={{ paddingLeft }}
					onClick={(e) => { e.stopPropagation(); toggle(); }}
				>
					{renderLabel}
					{item.children && renderChevron}
				</Mui.Button>
			);
		}
	}, [open, firstChild, sidebarCollapsed, classes.item, classes.inactive, classes.groupItemChildActive, childIsActive, paddingLeft, renderLabel, item.children, renderChevron, toggle]);

	const itemRow = item.label && (sidebarCollapsed || level > 0) && (
		<div
			className={classNames(classes.itemWrapper, item.className)}
			style={{
				backgroundColor: `rgba(255, 255, 255, ${backgroundOpacity})`,
			}}
			onClick={toggle}
		>
			{item.path && !item.target && renderInternalLink}
			{item.path && item.target && renderExternalLink}
			{!item.path && item.children && renderGroup}
		</div>
	);

	const collapseSection = item.children && (
		<Mui.Collapse in={open}>
			{item.children.map((item, index) => (
				<SidebarMenuItem key={index} user={user} item={item} />
			))}
		</Mui.Collapse>
	);

	if (item.children && level > 0) {
		return (
			<div className={classNames(classes.groupContainer, (open || childIsActive) && classes.groupContainerActive)}>
				{itemRow}
				{collapseSection}
			</div>
		);
	}

	return (
		<>
			{itemRow}
			{collapseSection}
		</>
	);
};

export const SidebarMenuItem = withMediaQuery("(max-width:600px)")(
	withStyles(Router.withRouter(Component), styles)
);